function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = (Number(cents) / 100).toFixed(2);
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';
  const key = (formatString.match(placeholder) || [])[1] || 'amount';
  let amount = value;
  if (key === 'amount_no_decimals') amount = String(Math.round(Number(cents) / 100));
  if (key === 'amount_with_comma_separator') amount = value.replace('.', ',');
  return formatString.replace(placeholder, amount);
}

function parseAvailable(value) {
  if (value === true || value === false) return value;
  if (value == null) return true;
  const normalized = String(value).trim().toLowerCase();
  return normalized !== 'false' && normalized !== '0' && normalized !== '';
}

document.addEventListener('alpine:init', () => {
  Alpine.store('cart', {
    open: false,
    loading: false,
    itemCount: window.theme?.cartCount || 0,
    items: [],
    total: 0,
    async refresh() {
      try {
        const response = await fetch(window.theme.routes.cart, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Cart refresh failed');
        const cart = await response.json();
        this.items = cart.items || [];
        this.itemCount = cart.item_count || 0;
        this.total = cart.total_price || 0;
        return cart;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async add(id, quantity) {
      this.loading = true;
      try {
        const response = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(id), quantity: Number(quantity) || 1 }]
          })
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Add to cart failed');
        }
        await this.refresh();
        this.open = true;
        return true;
      } catch (error) {
        console.error(error);
        window.alert(error.message || 'Could not add to cart');
        return false;
      } finally {
        this.loading = false;
      }
    },
    async change(key, quantity) {
      try {
        const response = await fetch(window.theme.routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: Number(quantity) })
        });
        if (!response.ok) throw new Error('Cart update failed');
        await this.refresh();
      } catch (error) {
        console.error(error);
      }
    },
    money(cents) {
      return formatMoney(cents, window.theme.moneyFormat);
    },
    imageUrl(item) {
      if (!item?.image) return window.theme?.placeholderImage || '';
      const joiner = item.image.includes('?') ? '&' : '?';
      return `${item.image}${joiner}width=240`;
    },
    countLabel() {
      const n = this.itemCount || 0;
      return n === 1 ? '1 item' : `${n} items`;
    }
  });

  Alpine.store('account', {
    open: false,
    view: 'login'
  });

  Alpine.store('search', {
    open: false,
    query: '',
    results: [],
    loading: false,
    async find() {
      const q = this.query.trim();
      if (q.length < 2) {
        this.results = [];
        return;
      }
      this.loading = true;
      try {
        const suggest = `${window.theme.routes.predictiveSearch}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8`;
        const response = await fetch(suggest, { headers: { Accept: 'application/json' } });
        const data = await response.json();
        this.results = data.resources?.results?.products || [];
      } catch (error) {
        this.results = [];
      } finally {
        this.loading = false;
      }
    }
  });

  Alpine.data('sidebarNav', () => ({
    active: '',
    locked: false,
    menuOpen: false,
    observer: null,
    offset() {
      if (window.innerWidth > 768) return 20;
      const chrome = document.querySelector('.site-chrome');
      const nav = this.$refs.mobileNav;
      return (chrome?.offsetHeight || 58) + (nav?.offsetHeight || 64) + 10;
    },
    activeTitle() {
      const link = this.$root.querySelector(`.home-menu a[href="${this.active}"]`);
      return link ? link.textContent.trim() : '';
    },
    init() {
      const first = this.$root.querySelector('.collection-panel');
      if (first) this.active = `#${first.id}`;
      this.$nextTick(() => {
        this.setupObserver();
        const hash = window.location.hash || this.$root.dataset.scrollTarget;
        if (hash) this.scrollTo(hash, window.theme.pageType === 'index');
      });
      window.addEventListener(
        'scroll',
        () => {
          if (this.menuOpen) this.menuOpen = false;
        },
        { passive: true }
      );
    },
    setupObserver() {
      const sections = this.$root.querySelectorAll('.collection-panel');
      if (!sections.length) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (this.locked) return;
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) this.active = `#${visible[0].target.id}`;
        },
        { rootMargin: '-18% 0px -62% 0px', threshold: [0, 0.2, 0.45] }
      );
      sections.forEach((section) => this.observer.observe(section));
    },
    choose(hash) {
      this.menuOpen = false;
      this.scrollTo(hash);
    },
    scrollTo(hash, updateHash = true) {
      if (!hash) return;
      const el = document.querySelector(hash);
      if (!el) return;
      this.active = hash;
      this.locked = true;
      const top = el.getBoundingClientRect().top + window.scrollY - this.offset();
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      if (updateHash && window.theme.pageType === 'index') {
        history.replaceState(null, '', hash);
      }
      window.setTimeout(() => {
        this.locked = false;
      }, 750);
    }
  }));

  Alpine.data('productForm', () => ({
    product: { variants: [] },
    variantId: 0,
    qty: 1,
    price: 0,
    available: true,
    sku: '',
    barcode: '',
    handle: '',
    shareUrl: '',
    tab: 'description',
    wishOn: false,
    wishKey: 'zaz-wishlist',
    adding: false,
    atcSuccess: false,
    copied: false,
    init() {
      const jsonId = this.$el.dataset.productJson;
      const jsonEl = jsonId ? document.getElementById(jsonId) : null;
      if (jsonEl) {
        try {
          this.product = JSON.parse(jsonEl.textContent);
        } catch (error) {
          console.error('Invalid product JSON', error);
        }
      }

      this.variantId = Number(this.$el.dataset.variantId) || this.product.variants?.[0]?.id || 0;
      this.price = Number(this.$el.dataset.price) || 0;
      this.available = parseAvailable(this.$el.dataset.available);
      this.handle = this.$el.dataset.handle || this.product.handle || '';
      this.sku = this.$el.dataset.sku || '';
      this.barcode = this.$el.dataset.barcode || '';
      this.shareUrl = this.$el.dataset.shareUrl || window.location.href;
      this.tab = this.$el.dataset.tab || 'description';

      const current = this.product.variants?.find((item) => String(item.id) === String(this.variantId));
      if (current) {
        this.price = current.price;
        this.available = Boolean(current.available);
        this.sku = current.sku || '';
        this.barcode = current.barcode || '';
      }

      try {
        const list = JSON.parse(localStorage.getItem(this.wishKey) || '[]');
        this.wishOn = list.includes(this.handle);
      } catch (error) {
        this.wishOn = false;
      }
    },
    get buttonLabel() {
      if (!this.available) return window.theme.strings.soldOut;
      return `${window.theme.strings.addToCart} – ${formatMoney(this.price * this.qty, window.theme.moneyFormat)}`;
    },
    get atcLabel() {
      if (!this.available) return window.theme.strings.soldOut;
      if (this.atcSuccess) return window.theme.strings.added;
      if (this.adding) return window.theme.strings.adding || 'Adding…';
      return this.buttonLabel;
    },
    setVariant(id) {
      const variant = this.product.variants.find((item) => String(item.id) === String(id));
      if (!variant) return;
      this.variantId = variant.id;
      this.price = variant.price;
      this.available = Boolean(variant.available);
      this.sku = variant.sku || '';
      this.barcode = variant.barcode || '';
      if (variant.featured_image && this.$refs.mainImage) {
        this.$refs.mainImage.src = variant.featured_image;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      history.replaceState({}, '', url.toString());
    },
    toggleWish() {
      if (!this.handle) return;
      let list = [];
      try {
        list = JSON.parse(localStorage.getItem(this.wishKey) || '[]');
      } catch (error) {
        list = [];
      }
      const next = list.includes(this.handle)
        ? list.filter((item) => item !== this.handle)
        : [...list, this.handle];
      localStorage.setItem(this.wishKey, JSON.stringify(next));
      this.wishOn = next.includes(this.handle);
    },
    async share() {
      const url = this.shareUrl || window.location.href;
      const title = document.title;
      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          return;
        } catch (error) {
          if (error.name === 'AbortError') return;
        }
      }
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          const input = document.createElement('input');
          input.value = url;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }
        this.copied = true;
        window.setTimeout(() => {
          this.copied = false;
        }, 1600);
      } catch (error) {
        window.prompt('Copy this link', url);
      }
    },
    async submit() {
      if (!this.available || this.adding || !this.variantId) return;
      this.adding = true;
      this.atcSuccess = false;
      try {
        const ok = await Alpine.store('cart').add(this.variantId, this.qty);
        if (ok) {
          this.atcSuccess = true;
          await new Promise((resolve) => window.setTimeout(resolve, 650));
        }
      } finally {
        this.adding = false;
        this.atcSuccess = false;
      }
    }
  }));

  Alpine.data('backToTop', () => ({
    show: false,
    progress: 0,
    init() {
      window.addEventListener('scroll', () => this.update(), { passive: true });
      this.update();
    },
    update() {
      const scrollTop = window.scrollY;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      this.show = scrollTop > 100;
      this.progress = max > 0 ? (scrollTop / max) * 360 : 0;
    },
    go() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }));
});
